import React, { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import {
  FiRefreshCw,
  FiSettings,
  FiPlay,
  FiPlus,
  FiTrash2,
  FiSlash,
  FiAlertTriangle,
  FiCalendar,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import { fetchTaskSchedule, removeOccurrence, pullOccurrence, generateSchedule } from "../../../utils/taskApi";
import ScheduleSettingsModal from "./ScheduleSettingsModal";
import PinTaskModal from "./PinTaskModal";
import ConfirmActionModal from "../../events/components/ConfirmActionModal";
import {
  TASK_TYPE_LABEL,
  TASK_TYPE_BADGE,
  OCCURRENCE_STATUS_LABEL,
  OCCURRENCE_STATUS_BADGE,
  SKIP_REASON_LABEL,
  formatDay,
  utcDay,
} from "../taskConstants";

/**
 * The Schedule tab — the screen that makes the automation legible.
 *
 * A week of columns, each an occurrence list. The point is that an admin can
 * see what users will get before they get it, and reach in: pin something the
 * generator missed, remove something not yet live, kill something live whose
 * link died.
 *
 * The three actions are deliberately not interchangeable. Remove deletes a
 * publication that has not happened; pull stops one that is happening and
 * keeps the record, because people may already have earned points from it.
 */

const WINDOW_DAYS = 7;

const OccurrenceCard = ({ occurrence, onRemove, onPull }) => {
  const isLive = occurrence.status === "live";
  const isScheduled = occurrence.status === "scheduled";

  return (
    <div
      className={`rounded-xl border p-3 bg-white transition ${
        occurrence.status === "pulled" ? "border-rose-100 opacity-75" : "border-gray-200 hover:border-gray-300"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="font-semibold text-gray-800 text-xs leading-snug">{occurrence.title}</p>
        <span
          className={`text-[10px] font-medium px-2 py-0.5 rounded-full border shrink-0 ${
            OCCURRENCE_STATUS_BADGE[occurrence.status] || OCCURRENCE_STATUS_BADGE.scheduled
          }`}
        >
          {OCCURRENCE_STATUS_LABEL[occurrence.status] || occurrence.status}
        </span>
      </div>

      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${TASK_TYPE_BADGE}`}>
          {TASK_TYPE_LABEL[occurrence.task_type] || occurrence.task_type}
        </span>
        <span className="text-[10px] text-gray-500">{occurrence.reward_points} pts</span>
        {occurrence.completed_count > 0 && (
          <span className="text-[10px] text-emerald-600 font-medium">{occurrence.completed_count} done</span>
        )}
      </div>

      {occurrence.pulled_reason && (
        <p className="text-[10px] text-rose-600 mt-2 italic">Pulled: {occurrence.pulled_reason}</p>
      )}

      {(isScheduled || isLive) && (
        <div className="flex items-center gap-1 mt-2.5 pt-2.5 border-t border-gray-100">
          {isScheduled && (
            <button
              onClick={() => onRemove(occurrence)}
              className="flex items-center gap-1 text-[10px] font-medium text-gray-500 hover:text-rose-600 px-2 py-1 rounded-lg hover:bg-rose-50 transition"
              title="Take it off this day — only possible before it goes live"
            >
              <FiTrash2 size={12} /> Remove
            </button>
          )}
          {isLive && (
            <button
              onClick={() => onPull(occurrence)}
              className="flex items-center gap-1 text-[10px] font-medium text-gray-500 hover:text-rose-600 px-2 py-1 rounded-lg hover:bg-rose-50 transition"
              title="Stop serving it now. Points already earned are kept."
            >
              <FiSlash size={12} /> Pull
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const ScheduleTab = ({ onChanged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [offset, setOffset] = useState(0);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [pinDay, setPinDay] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, loading: false });

  const from = utcDay(offset);
  const to = utcDay(offset + WINDOW_DAYS - 1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchTaskSchedule({ from, to });
      setData(res.data || null);
      setError(null);
    } catch (err) {
      setError(err.message || "Failed to load the schedule");
      toast.error(err.message || "Failed to load the schedule");
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    load();
  }, [load]);

  const refresh = () => {
    load();
    if (onChanged) onChanged();
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await generateSchedule();
      toast.success(res.message || "Schedule built");
      refresh();
    } catch (err) {
      // The common one is "the scheduler is switched off" — a message worth
      // showing verbatim, since the fix is one click away in Settings.
      toast.error(err.message || "Failed to build the schedule");
    } finally {
      setGenerating(false);
    }
  };

  const handleRemove = (occurrence) => {
    setConfirmModal({
      isOpen: true,
      title: "Remove from the schedule",
      message: `Take "${occurrence.title}" off ${formatDay(occurrence.day)}?`,
      subMessage: "It has not gone live yet, so nothing is lost — the task stays in the pool.",
      confirmText: "Remove",
      confirmColor: "red",
      icon: FiTrash2,
      loading: false,
      action: async () => {
        try {
          setConfirmModal((prev) => ({ ...prev, loading: true }));
          const res = await removeOccurrence(occurrence.id);
          toast.success(res.message || "Removed from the schedule");
          setConfirmModal((prev) => ({ ...prev, isOpen: false }));
          refresh();
        } catch (err) {
          toast.error(err.message || "Failed to remove it");
        } finally {
          setConfirmModal((prev) => ({ ...prev, loading: false }));
        }
      },
    });
  };

  const handlePull = (occurrence) => {
    setConfirmModal({
      isOpen: true,
      title: "Pull this task",
      message: `Stop serving "${occurrence.title}" right now?`,
      subMessage:
        occurrence.completed_count > 0
          ? `It disappears from the app immediately. The ${occurrence.completed_count} people who already completed it keep their points.`
          : "It disappears from the app immediately, and the slot is not refilled.",
      confirmText: "Pull it",
      confirmColor: "red",
      icon: FiSlash,
      loading: false,
      action: async () => {
        try {
          setConfirmModal((prev) => ({ ...prev, loading: true }));
          const res = await pullOccurrence(occurrence.id, "Pulled from the admin panel");
          toast.success(res.message || "Occurrence pulled");
          setConfirmModal((prev) => ({ ...prev, isOpen: false }));
          refresh();
        } catch (err) {
          toast.error(err.message || "Failed to pull it");
        } finally {
          setConfirmModal((prev) => ({ ...prev, loading: false }));
        }
      },
    });
  };

  const engineOff = data && data.is_enabled === false;
  const days = data?.days || [];

  return (
    <div className="space-y-6">
      <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setOffset((o) => o - WINDOW_DAYS)}
            className="p-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition"
            title="Previous week"
          >
            <FiChevronLeft size={15} />
          </button>
          <div className="px-2">
            <p className="text-xs font-semibold text-gray-700">
              {formatDay(from)} — {formatDay(to)}
            </p>
            <p className="text-[11px] text-gray-400">
              {offset === 0 ? "This week" : offset > 0 ? `${offset} days ahead` : `${-offset} days back`} ·{" "}
              {data ? `${data.daily_count} a day` : "—"}
            </p>
          </div>
          <button
            onClick={() => setOffset((o) => o + WINDOW_DAYS)}
            className="p-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition"
            title="Next week"
          >
            <FiChevronRight size={15} />
          </button>
          {offset !== 0 && (
            <button onClick={() => setOffset(0)} className="text-[11px] text-blue-600 hover:underline ml-1">
              Today
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={load}
            disabled={loading}
            className="p-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 rounded-lg transition"
            title="Refresh"
          >
            <FiRefreshCw size={15} className={loading ? "animate-spin text-blue-600" : ""} />
          </button>
          <button
            onClick={() => setSettingsOpen(true)}
            className="px-3 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 text-xs font-semibold rounded-lg transition flex items-center gap-2"
          >
            <FiSettings size={14} /> Settings
          </button>
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-xl transition shadow-sm shadow-blue-600/20 flex items-center gap-2 disabled:opacity-50"
          >
            <FiPlay size={14} /> {generating ? "Building..." : "Build now"}
          </button>
        </div>
      </div>

      {engineOff && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
          <FiAlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={18} />
          <div className="text-sm flex-1">
            <p className="font-semibold text-amber-800">The daily scheduler is switched off.</p>
            <p className="text-amber-700 text-xs mt-1">
              Nothing is being published, so the app's Tasks screen shows only what you pin by hand. Turn it on
              in Settings, then press Build now to fill today.
            </p>
          </div>
          <button
            onClick={() => setSettingsOpen(true)}
            className="px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold rounded-lg transition shrink-0"
          >
            Open Settings
          </button>
        </div>
      )}

      {error && !loading && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-5 py-12 text-center">
          <div className="flex flex-col items-center gap-2">
            <FiAlertTriangle size={28} className="text-rose-300" />
            <p className="font-medium text-gray-600">{error}</p>
            <button onClick={load} className="text-xs text-blue-600 hover:underline mt-1">
              Try again
            </button>
          </div>
        </div>
      )}

      {loading && !data ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-5 py-16 text-center text-gray-400">
          <div className="flex flex-col items-center gap-2">
            <FiRefreshCw className="animate-spin text-blue-600" size={20} />
            <span className="text-sm">Loading the schedule...</span>
          </div>
        </div>
      ) : (
        !error && (
          <div className="overflow-x-auto pb-2">
            <div className="grid grid-flow-col auto-cols-[minmax(220px,1fr)] gap-3 min-w-full">
              {days.map((column) => {
                const canEdit = !column.is_past;
                return (
                  <div
                    key={column.day}
                    className={`rounded-2xl border p-3 flex flex-col gap-3 ${
                      column.is_today ? "border-blue-300 bg-blue-50/40" : "border-gray-100 bg-white"
                    } shadow-sm`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className={`text-xs font-bold ${column.is_today ? "text-blue-700" : "text-gray-700"}`}>
                          {formatDay(column.day)}
                          {column.is_today && <span className="ml-1.5 font-medium">· Today</span>}
                        </p>
                        <p className="text-[11px] text-gray-400">
                          {column.filled} of {data.daily_count} filled
                        </p>
                      </div>
                      {column.empty_slots > 0 && !column.is_past && (
                        <span className="text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                          {column.empty_slots} free
                        </span>
                      )}
                    </div>

                    <div className="flex flex-col gap-2">
                      {column.occurrences.length === 0 ? (
                        <div className="rounded-xl border border-dashed border-gray-200 px-3 py-6 text-center">
                          <FiCalendar size={18} className="text-gray-300 mx-auto mb-1.5" />
                          <p className="text-[11px] text-gray-400">
                            {column.is_past ? "Nothing ran" : "Nothing scheduled"}
                          </p>
                        </div>
                      ) : (
                        column.occurrences.map((occurrence) => (
                          <OccurrenceCard
                            key={occurrence.id}
                            occurrence={occurrence}
                            onRemove={handleRemove}
                            onPull={handlePull}
                          />
                        ))
                      )}
                    </div>

                    {canEdit && (
                      <button
                        onClick={() => setPinDay(column.day)}
                        className="mt-auto flex items-center justify-center gap-1.5 text-[11px] font-medium text-gray-500 hover:text-blue-600 border border-dashed border-gray-200 hover:border-blue-300 rounded-xl py-2 transition"
                      >
                        <FiPlus size={13} /> Pin a task
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )
      )}

      <p className="text-[11px] text-gray-400 flex items-center gap-1.5">
        <FiAlertTriangle size={12} />
        Days are UTC, matching the scheduler. {SKIP_REASON_LABEL.empty_pool} on a day usually means cooldowns,
        not an empty catalogue.
      </p>

      <ScheduleSettingsModal isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} onSaved={refresh} />

      <PinTaskModal
        isOpen={!!pinDay}
        fixedDay={pinDay}
        onClose={() => setPinDay(null)}
        onPinned={refresh}
      />

      <ConfirmActionModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.action}
        title={confirmModal.title}
        message={confirmModal.message}
        subMessage={confirmModal.subMessage}
        confirmText={confirmModal.confirmText}
        confirmColor={confirmModal.confirmColor}
        icon={confirmModal.icon}
        loading={confirmModal.loading}
      />
    </div>
  );
};

export default ScheduleTab;
